import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contentPagesApi } from '../api/contentPagesApi';
import type { CreateContentPagePayload, UpdateContentPagePayload } from '../types';

/**
 * Keyed by tenant (not just id) so switching the selected merchant never serves a
 * stale cache from a different tenant — the backend scopes every route to
 * `x-tenant-id` and there is no cross-tenant listing here.
 */
const listKey = (tenantId: string | null) => ['content-pages', tenantId] as const;
const detailKey = (tenantId: string | null, id: string) => ['content-page', tenantId, id] as const;

export function useContentPages(tenantId: string | null) {
  return useQuery({
    queryKey: listKey(tenantId),
    queryFn: contentPagesApi.list,
    enabled: !!tenantId,
  });
}

export function useContentPage(tenantId: string | null, id: string | undefined) {
  return useQuery({
    queryKey: detailKey(tenantId, id ?? ''),
    queryFn: () => contentPagesApi.get(id as string),
    enabled: !!tenantId && !!id,
  });
}

export function useCreateContentPage(tenantId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateContentPagePayload) => contentPagesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(tenantId) }),
  });
}

export function useUpdateContentPage(tenantId: string | null, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateContentPagePayload) => contentPagesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(tenantId) });
      qc.invalidateQueries({ queryKey: detailKey(tenantId, id) });
    },
  });
}

export function usePublishContentPage(tenantId: string | null, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => contentPagesApi.publish(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(tenantId) });
      qc.invalidateQueries({ queryKey: detailKey(tenantId, id) });
    },
  });
}

export function useDeleteContentPage(tenantId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contentPagesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(tenantId) }),
  });
}
